import { useWindowSize } from '../../../../hooks/useWindowSize';
import { percentageToTwoDecimalPlaces } from '../../../../utilities/numberFormatters';
import GenericLineChart from '../../../charts/GenericLineChart';
import GridItem from '../../../layout/GridComponents/GridItem';
import {
  SubRedPanelView,
  SubscriptionsRedemptionsReport,
} from './subscriptionsRedemptions.types';
import { SubRedPanelHeader } from './SubRedViewToggle';
import {
  PANEL_CARD_STYLE,
  resolveChartSeries,
  SUB_RED_PANEL_CONFIG,
} from './utils/subRedPanelViews';

interface SubscriptionsRedemptionsChartProps<T extends SubRedPanelView> {
  data: SubscriptionsRedemptionsReport;
  view: T;
  views: T[];
  onViewChange: (view: T) => void;
}

function buildChartData(
  subsRedsData: SubscriptionsRedemptionsReport,
  view: SubRedPanelView,
) {
  const rows = resolveChartSeries(subsRedsData, view).slice(1);
  return rows.flatMap((value) =>
    typeof value[1] === 'number'
      ? [{ name: value[0], value: value[1] as number }]
      : [],
  );
}

function SubscriptionsRedemptionsChart<T extends SubRedPanelView>({
  data,
  view,
  views,
  onViewChange,
}: SubscriptionsRedemptionsChartProps<T>) {
  const config = SUB_RED_PANEL_CONFIG[view];
  const chartData = buildChartData(data, view);
  const { height } = useWindowSize();
  const title = config.chartTitle;
  const chartHeight = Math.max(height * 0.38, 280);

  return (
    <GridItem
      xs={6}
      card
      cardStyle={PANEL_CARD_STYLE}
      style={{ alignSelf: 'stretch' }}
    >
      <SubRedPanelHeader
        title={title}
        view={view}
        views={views}
        onViewChange={onViewChange}
      />

      {chartData.length === 0 ? (
        <p className="p-4 text-sm">No chart data.</p>
      ) : (
        <GenericLineChart
          key={view}
          title={title}
          data={chartData}
          export
          id={config.chartId}
          xAxisDataKey="name"
          yAxisDomain={yAxisDomain(chartData)}
          yAxisTickFormatter={(value: number) =>
            percentageToTwoDecimalPlaces(value)
          }
          tooltipFormatter={(value: number) =>
            percentageToTwoDecimalPlaces(value)
          }
          lines={[
            {
              dataKey: 'value',
              color: config.color,
            },
          ]}
          width={'100%'}
          height={chartHeight}
          showDot={false}
          margin={{
            top: 10,
            bottom: 10,
            left: 10,
            right: 40,
          }}
          error={'No Subcriptions/Redemptions Data available'}
        />
      )}
    </GridItem>
  );
}

function yAxisDomain(chartData: { value: number }[]): [number, number] {
  let minvalue = chartData[0].value;
  let maxvalue = chartData[0].value;
  chartData.forEach((value) => {
    if (minvalue > value.value) minvalue = value.value;
    if (maxvalue < value.value) maxvalue = value.value;
  });
  return [
    minvalue === 0 ? -0.01 : minvalue - Math.abs(minvalue) * 0.1,
    maxvalue + Math.abs(maxvalue) * 0.1,
  ];
}

export default SubscriptionsRedemptionsChart;
